// SPDX-License-Identifier: GPL-3.0-or-later
// The floor zones in the scene. Each zone is one flat plane with a soft edge, drawn with additive
// blending just above the playfield art, so the art and its white text stay on top. The zone data
// and the text contrast rule are in vfx/zones.ts. This file needs no WebGL context to build.

import { AdditiveBlending, Group, Mesh, PlaneGeometry, ShaderMaterial } from "three";
import { colorOf } from "./vfx/palette.ts";
import type { FloorZone } from "./vfx/zones.ts";

const VERTEX = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAGMENT = `
varying vec2 vUv;
uniform vec3 uColor;
uniform float uAlpha;
void main() {
  vec2 d = abs(vUv - 0.5) * 2.0;
  float soft = smoothstep(1.0, 0.4, max(d.x, d.y));
  gl_FragColor = vec4(uColor, uAlpha * soft);
  #include <colorspace_fragment>
}
`;

/** Make the material of one zone. */
function zoneMaterial(zone: FloorZone): ShaderMaterial {
  const material = new ShaderMaterial({
    uniforms: { uColor: { value: colorOf(zone.hex).clone() }, uAlpha: { value: zone.alpha } },
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  material.name = `floor-zone:${zone.name}`;
  return material;
}

/**
 * Build the zones. `height` is the height of the playfield surface in scene units. The scene frame
 * is x right, y up, z toward the player (table y).
 */
export function createFloorZones(zones: readonly FloorZone[], height: number): Group {
  const group = new Group();
  group.name = "floor-zones";
  const geometry = new PlaneGeometry(1, 1);
  geometry.rotateX(-Math.PI / 2);
  for (const zone of zones) {
    const mesh = new Mesh(geometry, zoneMaterial(zone));
    mesh.name = `floor-zone-${zone.name}`;
    mesh.scale.set(zone.w, 1, zone.h);
    mesh.position.set(zone.x, height + 0.15, zone.y);
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.renderOrder = 1;
    group.add(mesh);
  }
  return group;
}
